import type { RailwayColumn, RailwayDraft, RailwayFilters, RailwayPurpose, RailwayQuery, RailwaySeat, RailwaySort, RailwayStation, RailwayStationsData, RailwayTrain } from "../types";

/** 根据本地筛选返回符合条件的车次。 */
export function filterTickets(trains: RailwayTrain[], filters: RailwayFilters): RailwayTrain[] {
  const [start, end] = filters.departure.split("-").map(timeMinutes);
  return trains.filter((train) => {
    const prefix = train.train_code.charAt(0);
    const type = "GC".includes(prefix) ? "GC" : "DZTK".includes(prefix) ? prefix : "other";
    const departure = timeMinutes(train.depart_time);
    return ((!filters.trainTypes.length && !filters.features.length) || filters.trainTypes.includes(type) || filters.features.some((feature) => train.features.includes(feature)))
      && (!filters.origins.length || filters.origins.includes(train.origin_code))
      && (!filters.destinations.length || filters.destinations.includes(train.destination_code))
      && (!filters.seats.length || filters.seats.some((code) => train.seat_types.includes(code)))
      && (filters.departure === "00:00-24:00" || (departure >= start && departure <= end))
      && (!filters.discounted || train.discounted)
      && (!filters.exchange || (train.exchange && train.bookable))
      && (!filters.bookable || train.bookable);
  });
}

/** 校验已提交的车站与日期，构造往返对应查询。 */
export function buildQuery(draft: RailwayDraft, stations: RailwayStation[], minDate: string, maxDate: string, leg: "outbound" | "return" = "outbound"): RailwayQuery {
  const from = resolveStation(draft.from, stations);
  const to = resolveStation(draft.to, stations);
  if (!from || !to) throw new Error("请选择有效的出发车站和到达车站");
  if (from.code === to.code) throw new Error("出发车站和到达车站不能相同");
  if (draft.roundTrip && draft.returnDate < draft.date) throw new Error("返程日期不能早于出发日期");
  const isReturn = draft.roundTrip && leg === "return";
  const date = isReturn ? draft.returnDate : draft.date;
  if (!isDate(date) || date < minDate || date > maxDate) throw new Error(`查询日期须在 ${minDate} 至 ${maxDate} 之间`);
  return { date, from_station: isReturn ? to.code : from.code, to_station: isReturn ? from.code : to.code, purpose: draft.purpose };
}

/** 按指定字段返回排序副本。 */
export function sortTickets(trains: RailwayTrain[], sort: RailwaySort): RailwayTrain[] {
  const value = (train: RailwayTrain) => sort.key === "duration" ? train.duration_minutes : sort.key === "arrive" ? timeMinutes(train.arrive_time) : timeMinutes(train.depart_time);
  const ordered = [...trains]
    .sort((left, right) => (value(left) - value(right)) * (sort.direction === "asc" ? 1 : -1))
    .sort((left, right) => Number(Boolean(right.promotion_priority)) - Number(Boolean(left.promotion_priority)));
  const groups = new Map<string, RailwayTrain[]>();
  for (const train of ordered) groups.set(train.train_code, [...(groups.get(train.train_code) ?? []), train]);
  return [...groups.values()].flatMap((group) => sort.key === "duration" ? group : group.sort((left, right) => {
    const secondary = (train: RailwayTrain) => timeMinutes(sort.key === "depart" ? train.arrive_time : train.depart_time);
    return (value(left) - value(right) || secondary(left) - secondary(right)) * (sort.direction === "asc" ? 1 : -1);
  }));
}

/** 日期范围内生成每日标签。 */
export function dateRange(minDate: string, maxDate: string): string[] {
  if (!isDate(minDate) || !isDate(maxDate)) return [];
  const dates: string[] = [];
  for (let day = minDate; day <= maxDate && dates.length < 366; day = addDays(day, 1)) dates.push(day);
  return dates;
}

/** 普通票与学生票独立读取官方查询窗口。 */
export function queryWindow(metadata: RailwayStationsData, purpose: RailwayPurpose) {
  return purpose === "0X00"
    ? { min_date: metadata.student_min_date, max_date: metadata.student_max_date, window_source: metadata.student_window_source }
    : { min_date: metadata.min_date, max_date: metadata.max_date, window_source: metadata.window_source };
}

/** 日期条最多展示十五天，学生长窗口中仍包含当前已查询日期。 */
export function dateTabs(minDate: string, maxDate: string, selected: string): string[] {
  const dates = dateRange(minDate, maxDate);
  const selectedIndex = dates.indexOf(selected);
  const start = selectedIndex < 15 ? 0 : Math.min(selectedIndex, Math.max(0, dates.length - 15));
  return dates.slice(start, start + 15);
}

/** 保留人民币精度，缺失价格不显示为零。 */
export function formatPrice(price: number | null | undefined): string {
  return typeof price === "number" && Number.isFinite(price) ? `¥${price.toFixed(2).replace(/\.?0+$/, "")}` : "暂无票价";
}

/** 对站名或完整电报码做精确匹配，避免自由文本被当成站码请求。 */
export function resolveStation(value: string, stations: RailwayStation[]): RailwayStation | undefined {
  return stations.find((station) => station.name === value.trim() || station.code === value.trim().toUpperCase());
}

/** 同列可能包含不同席别，返回全部适用项供价格展示。 */
export function seatsForColumn(train: RailwayTrain, column: RailwayColumn): RailwaySeat[] {
  return train.seats.filter((seat) => seat.column === column && seat.availability !== "not_applicable");
}

/** 将钟点转为当天分钟数。 */
export function timeMinutes(time: string): number {
  const [hour, minute] = time.split(":").map(Number); return hour * 60 + minute;
}

/** 仅使用日历日运算，避免设备时区和夏令时改变日期。 */
export function addDays(date: string, count: number): string {
  const day = new Date(`${date}T00:00:00Z`); day.setUTCDate(day.getUTCDate() + count); return day.toISOString().slice(0, 10);
}

/** 取得中国时区当天日期。 */
export function chinaToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  return ["year", "month", "day"].map((part) => parts.find((item) => item.type === part)?.value).join("-");
}

/** 展示中文星期。 */
export function weekday(date: string): string { return `周${"日一二三四五六"[new Date(`${date}T00:00:00Z`).getUTCDay()]}`; }

/** 校验真实存在的 ISO 日历日。 */
export function isDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

/** 用 URL 初始化明确查询条件，默认中国当天北京至上海。 */
export function initialDraft(search: string): RailwayDraft {
  const params = new URLSearchParams(search); const today = chinaToday();
  return { from: params.get("from") || "北京", to: params.get("to") || "上海", date: params.get("date") || today, returnDate: params.get("returnDate") || params.get("date") || today, roundTrip: params.get("trip") === "round", purpose: params.get("purpose") === "0X00" ? "0X00" : "ADULT" };
}

/** 独立视图的返程标记只在往返模式生效，避免站点再次反转。 */
export function initialLeg(search: string): "outbound" | "return" {
  const params = new URLSearchParams(search);
  return params.get("trip") === "round" && params.get("leg") === "return" ? "return" : "outbound";
}

/** 独立视图携带原始已提交行程与方向，不从反向查询再次推导往返。 */
export function buildViewSearch(draft: RailwayDraft, leg: "outbound" | "return"): string {
  return new URLSearchParams({ from: draft.from, to: draft.to, date: draft.date, returnDate: draft.returnDate,
    purpose: draft.purpose, trip: draft.roundTrip ? "round" : "single", leg: draft.roundTrip ? leg : "outbound" }).toString();
}

/** 结果重建后移除不存在的动态选项，避免不可见条件持续过滤车次。 */
export function retainAvailableFilters(filters: RailwayFilters, trains: RailwayTrain[]): RailwayFilters {
  const origins = new Set(trains.map((train) => train.origin_code));
  const destinations = new Set(trains.map((train) => train.destination_code));
  const seats = new Set(trains.flatMap((train) => train.seat_types.split("")));
  const next = { ...filters, origins: filters.origins.filter((code) => origins.has(code)),
    destinations: filters.destinations.filter((code) => destinations.has(code)), seats: filters.seats.filter((code) => seats.has(code)) };
  return next.origins.length === filters.origins.length && next.destinations.length === filters.destinations.length && next.seats.length === filters.seats.length ? filters : next;
}
export { createFareSelection, fareSelectionId, moveFareSelection } from "./fareSelection";
